import axios, { isAxiosError } from 'axios';
import { QueryKey, useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '@redux/store';

import { url } from '@api-config';
import { useCallback } from 'react';
import { SUBMIT_BUTTON_LABELS } from '@3.0/Buttons';
import { openWarningModal } from '@redux/ErrorModal/error-modal-redux-slice';
import { getNoTablesFoundText } from '../IngestionStandaloneDropdown/helpers';
import { TDataSourceTypesAllowed } from '../IngestionStandaloneDropdown/IngestionByDataSource/IngestionByDataSource.constants';

type TTableData = { selectedCount: number; tableName: string };

export const TABLE_PAGE_LIMIT = 20;

export interface ITables {
  totalCount: number;
  pageNumber: number;
  data: TTableData[];
}

const initialData: ITables = { totalCount: 0, pageNumber: 1, data: [] };

const useFetchDataSourceTables = () => {
  const dispatch = useAppDispatch();

  const selectedDataSource = useAppSelector(
    (state) => state.ingestionDataSourceList.selectedDataSource
  );
  const searchTerm = useAppSelector((state) => state.ingestionDataSourceList.tables.searchTerm);
  const pageNumber = useAppSelector((state) => state.ingestionDataSourceList.tables.pageNumber);
  const pageLimit = useAppSelector((state) => state.ingestionDataSourceList.tables.pageLimit);

  const { name, id } = selectedDataSource || {};

  /**
   * fetches the data source tables
   * launches warning modal when there is an error
   */
  const fetchDataSourceTables = useCallback(async () => {
    try {
      const response = await axios.get(
        `${url.dataSet}/dataSource/${id}/tables?pageLimit=${pageLimit}&pageNumber=${pageNumber}&searchPattern=${searchTerm}&onlyMapped=true`
      );

      return response.data;
    } catch (error) {
      // first catch the error and display the warning
      let errorDetails = isAxiosError(error) ? error.response?.data : error;

      dispatch(
        openWarningModal({
          errorDetails: errorDetails?.additionalDetails || {},
          text: {
            messages: {
              primary:
                errorDetails?.message ||
                getNoTablesFoundText(selectedDataSource?.dataSourceType as TDataSourceTypesAllowed)
                  .errors.default,
            },
            buttons: { primary: SUBMIT_BUTTON_LABELS.ok },
          },
        })
      );

      // then continue to throw the error
      throw new Error(error?.toString());
    }
  }, [id, pageLimit, pageNumber, searchTerm, dispatch, selectedDataSource]);

  const query = useQuery<ITables, Error, ITables, QueryKey>({
    queryKey: ['fetchDataSourceTables', { name, id, searchTerm, pageNumber }],
    queryFn: fetchDataSourceTables,
    initialData,
    placeholderData: keepPreviousData,
    retry: false,
  });

  return query;
};

export default useFetchDataSourceTables;
